import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CustomInput from "../../components/CustomInput/CustomInput";
import regexList from "../../utils/regexList";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginUser } from "../../store/actions/auth.action";
import { login, getMicrosoftLoginUrl } from "../../endpoints/auth.service";
import { useSnackbar } from "notistack";
import TickOutline from "../../assets/svg/TickOutline";
import LoadingAnimation from "../../assets/svg/LoadingAnimation";
import { EnumUserTypes } from "../../utils/constants";
import { getPostLoginRoute } from "router/routerHelperFunctions";
import { getHiringCompanyDetails } from "@endpoints/company.service";
import { setCompanies } from "@store/actions/company.action";

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      isLoading: false,
      keepMeSignedIn: false,
    },
  });
  const fields = watch();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get("email") || params.get("login_hint");
    const sso = params.get("sso");

    console.log(params, email, sso);

    if (email && (sso === "1" || sso === "true")) {
      window.location.replace(`/sso-login?email=${encodeURIComponent(email)}`);
    }
  }, []);

  const handleLogin = async (fields) => {
    try {
      setValue("isLoading", true);
      const { data, success } = await login(fields);
      if (success) {
        const loginEmail = data?.user?.email || fields?.email;
        if (data?.user?.ssoEnabled && loginEmail) {
          localStorage.setItem("ssoEmail", loginEmail);
        } else {
          localStorage.removeItem("ssoEmail");
        }
        dispatch(loginUser(data));
        const target = getPostLoginRoute(data?.user);
        navigate(target, { replace: true });
        // navigate('/')
      }
      if (data?.user?.userType === EnumUserTypes.HIRING_COMPANY) {
        try {
          const { success, data } = await getHiringCompanyDetails();
          if (success) {
            dispatch(setCompanies(data));
          }
        } catch (error) {
          enqueueSnackbar(error, { variant: "error" });
        }
      }

      // if (!data?.user?.userName) {
      //   navigate("/generate-user-name", { replace: true });
      // }
    } catch (error) {
      if (error === "Otp not verified") {
        navigate(`/email-verification`, {
          state: {
            email: fields?.email,
          },
        });
        return;
      }
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setValue("isLoading", false);
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      setIsMicrosoftLoading(true);
      const { data, success } = await getMicrosoftLoginUrl(
        undefined,
        fields?.email,
      );
      if (success && data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("Unable to start Microsoft login.");
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  return (
    <div className="h-full flex">
      {/* Form Container */}
      <div className="flex flex-col justify-center items-center border-[#DDDDDD] border rounded-lg py-10 px-20 bg-white relative w-max h-max m-auto">
        <label className="font-semibold text-3xl mb-2">Login</label>
        <label className="text-light-black font-medium">
          Welcome back! Please login to access your account
        </label>
        <form
          className="flex flex-col w-full pt-10"
          onSubmit={handleSubmit(handleLogin)}
        >
          <CustomInput
            label={"Email"}
            name="email"
            placeholder={"Enter your Email"}
            rules={{
              required: "This field is required.",
              pattern: {
                value: regexList.email,
                message: "Please enter a valid email.",
              },
            }}
            control={control}
            errors={errors}
          />
          <div className="h-6" />
          <CustomInput
            label={"Password"}
            placeholder={"Enter your Password"}
            name="password"
            rules={{
              required: "This field is required.",
              pattern: {
                value: regexList.password,
                message: "Please enter a valid password.",
              },
            }}
            control={control}
            errors={errors}
          />

          {/* Checkbox container  */}
          <div className="flex justify-between items-center w-full mt-3 text-light-black">
            <div className="flex items-center gap-2">
              <div
                onClick={() =>
                  setValue("keepMeSignedIn", !fields?.keepMeSignedIn)
                }
                className="border-2 border-[#6F6F6F] h-4 w-4 flex items-center justify-center cursor-pointer"
              >
                {fields?.keepMeSignedIn ? (
                  <TickOutline className="w-1.5 h-1.5" fill="#6F6F6F" />
                ) : null}
              </div>
              <span className="font-montserrat font-medium text-light-black text-sm">
                Keep me signed in
              </span>
            </div>
            <span
              onClick={() => navigate("/forgot-password")}
              className="font-montserrat font-medium text-light-black text-xs cursor-pointer"
            >
              Forgot password?
            </span>
          </div>

          <button
            type="submit"
            className="bg-secondary flex items-center justify-center rounded-full mt-10 py-3"
          >
            {fields?.isLoading && <LoadingAnimation />}
            <span className="text-white font-medium">Login</span>
          </button>

          <div className="flex items-center gap-3 mt-6">
            <div className="h-px bg-[#DDDDDD] flex-1" />
            <span className="text-xs text-light-black">or</span>
            <div className="h-px bg-[#DDDDDD] flex-1" />
          </div>

          <button
            type="button"
            onClick={handleMicrosoftLogin}
            className="border border-[#DDDDDD] flex items-center justify-center rounded-full mt-6 py-3"
            disabled={isMicrosoftLoading}
          >
            {isMicrosoftLoading && <LoadingAnimation />}
            <span className="text-light-black font-medium">
              Sign in with Microsoft
            </span>
          </button>

          <span className="mx-auto text-sm mt-1 font-medium text-light-black">
            Don’t have an account?{" "}
            <span
              onClick={() => navigate("/register")}
              className="text-secondary cursor-pointer"
            >
              Register
            </span>
          </span>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
